export const OFFLINE_DB_NAME = 'libritui-offline-db';
export const OFFLINE_STORE_NAME = 'offline_books';
export const OFFLINE_DB_VERSION = 1;

/**
 * Initializes the IndexedDB for offline books.
 */
function getOfflineDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);

    request.onerror = (event) => {
      console.error('[OfflineDB] Error opening DB', event);
      reject(request.error);
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(OFFLINE_STORE_NAME)) {
        db.createObjectStore(OFFLINE_STORE_NAME, { keyPath: 'bookId' });
      }
    };
  });
}

/**
 * Saves a complete book payload for offline reading.
 */
export async function saveBookOffline(bookId: string, payload: any): Promise<void> {
  const db = await getOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([OFFLINE_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(OFFLINE_STORE_NAME);
    
    const request = store.put({
      bookId,
      payload,
      timestamp: Date.now()
    });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Retrieves an offline book payload.
 */
export async function getBookOffline(bookId: string): Promise<any | null> {
  const db = await getOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([OFFLINE_STORE_NAME], 'readonly');
    const store = transaction.objectStore(OFFLINE_STORE_NAME);
    const request = store.get(bookId);

    request.onsuccess = () => {
      const result = request.result;
      resolve(result ? result.payload : null);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Removes a book from offline storage.
 */
export async function removeBookOffline(bookId: string): Promise<void> {
  const db = await getOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([OFFLINE_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(OFFLINE_STORE_NAME);
    const request = store.delete(bookId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Checks if a book exists in offline storage.
 */
export async function isBookOffline(bookId: string): Promise<boolean> {
  const payload = await getBookOffline(bookId);
  return payload !== null;
}

/**
 * Gets all offline book IDs.
 */
export async function getAllOfflineBookIds(): Promise<string[]> {
  const db = await getOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([OFFLINE_STORE_NAME], 'readonly');
    const store = transaction.objectStore(OFFLINE_STORE_NAME);
    const request = store.getAllKeys();

    request.onsuccess = () => {
      resolve(request.result as string[]);
    };
    request.onerror = () => reject(request.error);
  });
}
